// SEC2-S3 (docs/build-briefs/SEC2-device-setup-code-binding.md section 4.7, DEC-321): the pure half of the
// on-screen setup code keypad. No DOM here, so a plain node:test file (test/ui-setup-code-entry.test.ts)
// can import it. ui/src/terminalSetup.ts draws the boxes and keys and calls these functions on each tap.
//
// The owner's ruling (DEC-321) is an in-page keypad; DEC-550 (2026-10-26) supersedes its "never with the
// device's own keyboard": both the keypad and a physical keyboard are accepted, and a keyboard key goes through
// the same appendKey. The keypad offers exactly the server's alphabet: digits and capitals without I, L,
// O and U (src/domain/deviceBinding.ts SETUP_CODE_ALPHABET, which a test compares). There is no key for a
// hyphen; the screen draws one after the fifth box.

/** The server's alphabet, in the order the keypad draws it. */
export const SETUP_CODE_KEYS = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

export const SETUP_CODE_LENGTH = 10;

/** Eight keys to a row, as the approved mockup draws them (0-7, 8-F, G-Q, R-Z). */
export const KEYPAD_ROWS: readonly (readonly string[])[] = [0, 8, 16, 24].map((start) => [
  ...SETUP_CODE_KEYS.slice(start, start + 8),
]);

/** One key, read the way the server reads typed text: capitals, I and L as 1, O as 0. Empty when it is not
 * one key of the alphabet. */
function normalizeKey(key: string): string {
  if (key.length !== 1) return "";
  const upper = key.toUpperCase().replace(/[IL]/, "1").replace(/O/, "0");
  return SETUP_CODE_KEYS.includes(upper) ? upper : "";
}

/** Adds one key. A key that is not in the alphabet, or an eleventh key, changes nothing. */
export function appendKey(code: string, key: string): string {
  if (code.length >= SETUP_CODE_LENGTH) return code;
  return code + normalizeKey(key);
}

/**
 * UX84 (DEC-336): what a pasted clipboard text becomes. Trimmed, upper-cased, spaces and dashes dropped, I and L
 * read as 1 and O as 0 (the way the server reads typed text, src/domain/deviceBinding.ts normalizeSetupCode),
 * every other character outside the keypad's alphabet dropped, and at most the first ten kept. Empty when
 * nothing usable is left, which the screen reports as a clipboard it could not use. Pasting is not typing: the
 * keypad stays the only way to type a code.
 */
export function normalizePastedCode(text: string): string {
  const cleaned = text
    .trim()
    .toUpperCase()
    .replace(/[\s\p{Pd}]/gu, "");
  let code = "";
  for (const char of cleaned) {
    code = appendKey(code, char);
    if (code.length >= SETUP_CODE_LENGTH) break;
  }
  return code;
}

export function deleteLast(code: string): string {
  return code.slice(0, -1);
}

export function isComplete(code: string): boolean {
  return code.length === SETUP_CODE_LENGTH;
}

export interface CodeBox {
  /** The key in this box, or "" while it is empty. */
  readonly char: string;
  /** True for the one box that takes the next key. None once the code is complete. */
  readonly active: boolean;
}

/** Always ten boxes, in order. */
export function codeBoxes(code: string): readonly CodeBox[] {
  const boxes: CodeBox[] = [];
  for (let index = 0; index < SETUP_CODE_LENGTH; index += 1) {
    boxes.push({ char: code.charAt(index), active: index === code.length });
  }
  return boxes;
}

/** The entry as XXXXX-XXXXX, with the hyphen only once there is a sixth character. */
export function formatEntered(code: string): string {
  return code.length > 5 ? `${code.slice(0, 5)}-${code.slice(5)}` : code;
}
